import type { Ref } from 'vue'
import type { TSportEvent } from 'markets-store'
import { sortBy } from '@st/utils'
import { buildEventsByEntityMap } from 'markets-store/helpers/filterCustomMenu'
import { useCustomMenu } from '@st/nuxt-markets-store/composables/useCustomMenu'

type MaybeRef<T> = Ref<T> | T
interface UseTopLiveEventsParams {
  // Фильтр по id событий, чтобы избежать дублирования с другими блоками главной страницы
  duplicateEventIds?: MaybeRef<number[]>
  // Фильтр по элементам кастом меню
  menuEntityIds?: MaybeRef<string[]>
  quantity?: MaybeRef<number>
}

interface UseTopLiveEventsReturn {
  topLiveEvents: Readonly<Ref<TSportEvent[]>>
}

export function useTopLiveEvents({
  duplicateEventIds = [],
  menuEntityIds = [],
  quantity = 30,
}: UseTopLiveEventsParams = {}): UseTopLiveEventsReturn {
  const liveEvents = useLiveEvents()
  const customMenu = useCustomMenu()

  const { getByIds } = useMarketsStore().sportEvents

  const eventsByEntity = computed(() =>
    buildEventsByEntityMap(customMenu.value, liveEvents.value),
  )

  const sortedLiveEvents = computed<TSportEvent[]>(() =>
    customMenu.value.menu.flatMap(({ entityId }) => {
      const ids = Array.from(eventsByEntity.value[entityId] ?? [])
      const events = getByIds(ids)
      return sortBy(events, (event) => [
        event.weight,
        event.scheduled,
        event.id,
      ])
    }),
  )

  const filteredLiveEvents = computed<TSportEvent[]>(() => {
    const entityIds = unref(menuEntityIds)
    const duplicateIds = new Set(unref(duplicateEventIds))

    return sortedLiveEvents.value.filter((event) => {
      if (duplicateIds.has(event.id)) return false
      if (entityIds?.length) {
        return entityIds.some(
          (entityId) => eventsByEntity.value[entityId]?.has(event.id),
        )
      }
      return true
    })
  })

  const topLiveEvents = computed<TSportEvent[]>(() =>
    filteredLiveEvents.value.slice(0, unref(quantity)),
  )

  return { topLiveEvents }
}
